import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CartService} from '../../../../services/cart/cart.service';
import {RouteService} from '../../../../services/route/route.service';
import {StorageService} from '../../../../services/storage/storage.service';
import {Subscription} from 'rxjs';
import {TicketSelectHeaderComponent} from '../ticket-select-header/ticket-select-header.component';
import {
  PlacesRequest,
  PriceClass,
  PriceType,
  Sector,
  LegacyTicket,
  EventPricingTicketType
} from '../../../../generated/sales-terminal-client';
import {DetailedPriceClass, DetailedSector} from '../ticket-select/ticket-select.component';
import {TICKETS_SHOW_EXACT_NUMBER_LIMIT} from '../../sales-terminal.constants';
import {EventService} from '../../../../services/event/event.service';
import {finalize} from 'rxjs/operators';

interface PriceTypeGroup {
  ticketCount: number;
  priceTypeId: number;
  priceTypeName: string;
  ticket: LegacyTicket;
}

@Component({
  selector: 'app-ticket-select-sector',
  templateUrl: './ticket-select-sector.component.html',
  styleUrls: ['./ticket-select-sector.component.scss']
})
export class TicketSelectSectorComponent implements OnInit, OnDestroy {
  public viewType = {
    VIEW_SECTORS: TicketSelectHeaderComponent.VIEW_SECTORS,
    VIEW_TICKETS: TicketSelectHeaderComponent.VIEW_TICKETS
  };
  public freePlacesLimit: number = TICKETS_SHOW_EXACT_NUMBER_LIMIT;
  public selectableSeatsLimit: number = 100;
  public clientNotificationEmail: string;
  private _subscriptions: Subscription[] = [];

  @Input() public hasVenueMap: boolean;
  @Input() public suggestEnabled: boolean;
  @Input() public selectedView: string;
  @Input() public enabledSectors: Sector[] = [];
  @Input() public selectedSector: DetailedSector;
  @Input() public eventId: number;

  @Output() selectSectorById: EventEmitter<number> = new EventEmitter<number>();
  @Output() hoverOnActiveSector: EventEmitter<number> = new EventEmitter<number>();
  @Output() backToSectorSelection: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateSuggest: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateCart: EventEmitter<any> = new EventEmitter<any>();
  @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _selectedPriceClassIds: number[] = [];
  @Input()
  public get selectedPriceClassIds(): number[] {
    return this._selectedPriceClassIds;
  }
  @Output()
  selectedPriceClassIdsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  public set selectedPriceClassIds(value: number[]) {
    this._selectedPriceClassIds = value;
    this.selectedPriceClassIdsChange.emit(this._selectedPriceClassIds);
  }

  private _suggestedTickets: Record<string, Record<string, number>> = {};
  @Input()
  public set suggestedTickets(value: Record<string, Record<string, number>>) {
    if (value == null) {
      value = {};
    }
    this._suggestedTickets = value;
    this.suggestedTicketsChange.emit(this._suggestedTickets);
  }
  public get suggestedTickets(): Record<string, Record<string, number>> {
    return this._suggestedTickets;
  }
  @Output() suggestedTicketsChange: EventEmitter<Record<string, Record<string, number>>> =
    new EventEmitter<Record<string, Record<string, number>>>();

  constructor(public routeService: RouteService,
              public eventService: EventService,
              private storageService: StorageService,
              public cartService: CartService) { }

  public ngOnInit(): void {
    if (this.routeService.isWindow) {
      this.clientNotificationEmail = this.storageService.get(CartService.STORAGE_CART_CLIENT_NOTIFICATION_EMAIL) || '';
    }
    this._subscriptions.push(this.storageService.changes.subscribe(({key, value}) => {
      if (key === CartService.STORAGE_CART_CLIENT_NOTIFICATION_EMAIL && this.routeService.isWindow) {
        this.clientNotificationEmail = value || '';
      }
    }));
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
    this._subscriptions = [];
  }

  public getPriceClassOtherOptions(priceClass: DetailedPriceClass): PriceType[] {
    if (priceClass.options?.length > 1) {
      return priceClass.options.filter((item, index) => index);
    }
    return [];
  }
  public getPriceClassDefaultOption(priceClass: DetailedPriceClass): PriceType {
    if (priceClass.options?.length) {
      return priceClass.options[0];
    }
    return null;
  }

  public togglePriceClassById(priceClassId: number): void {
    if (this.selectedPriceClassIds.includes(priceClassId)) {
      this.selectedPriceClassIds.splice(this.selectedPriceClassIds.indexOf(priceClassId), 1);
    } else {
      this.selectedPriceClassIds.push(priceClassId);
    }
  }

  public getPriceClassMaxTickets(priceClass: PriceClass): number {
    return this.cartService.calculateMaxTickets(priceClass.freePlaces === this.freePlacesLimit ? this.selectableSeatsLimit : priceClass.freePlaces);
  }

  public suggestedTicketsCountByClassType(priceClassId: number, priceTypeId: number): number {
    return this.suggestedTickets[priceClassId] && this.suggestedTickets[priceClassId][priceTypeId] || 0;
  }
  public changeTicketCount(priceClass: PriceClass, priceTypeId: number, action = 'update', inCount = 0): number {
    const limit = priceClass.freePlaces > TICKETS_SHOW_EXACT_NUMBER_LIMIT ? this.selectableSeatsLimit : priceClass.freePlaces;
    const count = inCount || this.suggestedTicketsCountByClassType(priceClass.id, priceTypeId);
    if (!this.suggestedTickets[priceClass.id]) {
      this.suggestedTickets[priceClass.id] = {};
    }
    if (!this.suggestedTickets[priceClass.id][priceTypeId]) {
      this.suggestedTickets[priceClass.id][priceTypeId] = 0;
    }
    if (action === 'add') {
      this.suggestedTickets[priceClass.id][priceTypeId]++;
    } else if (action === 'reduce') {
      this.suggestedTickets[priceClass.id][priceTypeId]--;
    } else if (action === 'update') {
      this.suggestedTickets[priceClass.id][priceTypeId] = count > 0 ? count : 0;
    }
    if (this.suggestedTickets[priceClass.id][priceTypeId] > limit) {
      this.suggestedTickets[priceClass.id][priceTypeId] = limit;
    }
    this.updateSuggest.emit();
    return this.suggestedTickets[priceClass.id][priceTypeId] || 0;
  }

  public get suggestedTicketsCount(): number {
    let total = 0;
    Object.keys(this.suggestedTickets).forEach(priceClassId => {
      Object.keys(this.suggestedTickets[priceClassId]).forEach(priceTypeId => {
        total += this.suggestedTickets[priceClassId][priceTypeId];
      });
    });
    return total;
  }
  public get limitExceeded(): boolean {
    return this.cartService.calculateMaxTickets(this.suggestedTicketsCount) <= 0;
  }

  public suggest(): void {
    const data: PlacesRequest = {
      eventId: this.eventId,
      sectorId: this.selectedSector.id,
      places: []
    };
    Object.keys(this.suggestedTickets).forEach(priceClassId => {
      Object.keys(this.suggestedTickets[priceClassId]).forEach(priceTypeId => {
        if (this.suggestedTickets[priceClassId][priceTypeId]) {
          data.places.push({
            priceClassId: parseFloat(priceClassId),
            priceTypeId: parseFloat(priceTypeId),
            amount: this.suggestedTickets[priceClassId][priceTypeId]
          });
        }
      });
    });
    this.addPlaces(data);
  }
  private addPlaces(data: PlacesRequest, priceTypeGroup?: PriceTypeGroup): void {
    this.loading.emit(true);
    this.suggestedTickets = {};
    this.cartService.addPlaces(data).pipe(
      finalize(() => this.loading.emit(false))
    ).subscribe(cart => {
      this.cartService.lastEventId = this.eventId;
      this.updateCart.emit();
      if (priceTypeGroup) {
        priceTypeGroup.ticketCount = priceTypeGroup.ticketCount + data.places.length;
      } else {
        this.selectSectorById.emit(this.selectedSector?.id);
      }
    });
  }
}
